// Routing and the gates in front of it: signed in? platform admin (with second factor)? member of an active
// organization? The database enforces all of this again; the gates only pick the right screen.
import { HashRouter, Navigate, Route, Routes } from "react-router";
import { isAdminRole, isManagerRole } from "./lib/format";
import { Layout } from "./app/Layout";
import { useSession } from "./app/session";
import { Loading } from "./app/ui";
import { AiSetup } from "./pages/AiSetup";
import { CallNotes } from "./pages/CallNotes";
import { Review } from "./pages/Review";
import { Mfa, Onboarding, Problem, Suspended } from "./pages/Auth";
import { SignIn } from "./pages/SignIn";
import { CallDetail } from "./pages/CallDetail";
import { Calls } from "./pages/Calls";
import { Digests } from "./pages/Digests";
import { Health } from "./pages/Health";
import { Import } from "./pages/Import";
import { IntegrationPage, Integrations } from "./pages/Integrations";
import { LeadDetail } from "./pages/LeadDetail";
import { Leads } from "./pages/Leads";
import { Objection } from "./pages/Objection";
import { Overview } from "./pages/Overview";
import { Pipeline } from "./pages/Pipeline";
import { PlatformGrowth, PlatformRevenue, PlatformTech } from "./pages/Platform";
import { PlatformCustomers, PlatformDashboard } from "./pages/PlatformHome";
import { PlatformOrg } from "./pages/PlatformOrg";
import { Settings } from "./pages/Settings";
import { Team } from "./pages/Team";
import { Today } from "./pages/Today";
import { Usage } from "./pages/Usage";

export function App() {
  const { loading, signedIn, status, error } = useSession();

  if (loading) {
    return (
      <div className="auth">
        <Loading />
      </div>
    );
  }
  if (!signedIn) {
    return (
      <HashRouter>
        <SignIn />
      </HashRouter>
    );
  }
  if (error || !status) {
    return <Problem error={error ?? "Could not load your account."} />;
  }

  if (status.platform_admin) {
    return (
      <HashRouter>
        {status.platform_ready
          ? (
            <Routes>
              <Route path="/org/:id/:tab?" element={<PlatformOrg />} />
              <Route path="/customers/:segment?" element={<PlatformCustomers />} />
              <Route path="/revenue" element={<PlatformRevenue />} />
              <Route path="/growth" element={<PlatformGrowth />} />
              <Route path="/tech" element={<PlatformTech />} />
              <Route path="*" element={<PlatformDashboard />} />
            </Routes>
          )
          : <Mfa />}
      </HashRouter>
    );
  }
  if (!status.member) {
    return (
      <HashRouter>
        <Onboarding />
      </HashRouter>
    );
  }
  if (status.member.org_status === "suspended") return <Suspended />;

  const role = status.member.role;
  const home = isManagerRole(role) ? "/overview" : "/today";
  return (
    <HashRouter>
      <Layout>
        <Routes>
          <Route path="/" element={<Navigate to={home} replace />} />
          <Route path="/overview" element={<Overview />} />
          <Route path="/today" element={<Today />} />
          <Route path="/leads" element={<Leads />} />
          <Route path="/leads/:id" element={<LeadDetail />} />
          <Route path="/pipeline" element={<Pipeline />} />
          {isManagerRole(role) && <Route path="/calls" element={<Calls />} />}
          <Route path="/calls/:id" element={<CallDetail />} />
          <Route path="/objections/:category" element={<Objection />} />
          {isManagerRole(role) && <Route path="/team" element={<Team />} />}
          {isManagerRole(role) && <Route path="/health" element={<Health />} />}
          {isManagerRole(role) && <Route path="/digests" element={<Digests />} />}
          {isManagerRole(role) && <Route path="/import" element={<Import />} />}
          {isAdminRole(role) && <Route path="/usage" element={<Usage />} />}
          {isAdminRole(role) && <Route path="/ai" element={<AiSetup />} />}
          {isAdminRole(role) && <Route path="/call-notes" element={<CallNotes />} />}
          <Route path="/review" element={<Review />} />
          {isAdminRole(role) && <Route path="/settings" element={<Settings />} />}
          {isAdminRole(role) && <Route path="/integrations" element={<Integrations />} />}
          {isAdminRole(role) && <Route path="/integrations/:key" element={<IntegrationPage />} />}
          <Route path="*" element={<Navigate to={home} replace />} />
        </Routes>
      </Layout>
    </HashRouter>
  );
}
